import { useEffect, useState, useCallback } from 'react'
import {
  ShieldAlert, Plus, Search, RefreshCw, AlertTriangle,
  Shield, ChevronRight, Pencil, Trash2, CheckCircle2,
  Database, Activity, TrendingUp, Package, Clock,
  Filter, ChevronDown, BarChart3,
} from 'lucide-react'
import { AddProductModal } from '@/components/dr/AddProductModal'
import type { ProductFormData } from '@/components/dr/AddProductModal'
import styles from './DisasterRecoveryPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

// ── Types ─────────────────────────────────────────────────────────────────────

interface Product {
  id: number
  product_name: string
  product_code: string
  environment: string
  criticality: string
  business_impact: string
  dr_strategy: string
  automation_readiness: string
  primary_region: string
  dr_region: string
  rpo_target_hours: string
  rto_target_hours: string
  business_owner: string
  technical_owner: string
  recovery_sequence: number
  dr_config_complete: boolean
  dr_readiness_score: number
  created_at: string
}

interface Executive {
  total_products: number
  products_without_dr: number
  products_failed_rpo_rto_last_drill: number
  upcoming_drills_30d: number
  overdue_action_items: number
  backup_failures_24h: number
  dr_readiness_score: number
  compliance_score: number
}

// ── Badge helpers ─────────────────────────────────────────────────────────────

const ENV_COLORS: Record<string, string> = {
  PRODUCTION: '#1A7B7E', QA: '#7C3AED', UAT: '#D97706', DEV: '#6B7280', DEMO: '#0891B2',
}
const CRIT_COLORS: Record<string, string> = {
  CRITICAL: '#DC2626', HIGH: '#EA580C', MEDIUM: '#D97706', LOW: '#16A34A',
}
const STRATEGY_LABELS: Record<string, string> = {
  ACTIVE_ACTIVE: 'Active-Active', ACTIVE_PASSIVE: 'Active-Passive',
  PILOT_LIGHT: 'Pilot Light', WARM_STANDBY: 'Warm Standby', BACKUP_RESTORE: 'Backup & Restore',
}
const AUTO_LABELS: Record<string, string> = {
  FULL: 'Full Auto', PARTIAL: 'Partial', MANUAL: 'Manual', NONE: 'None',
}

function EnvBadge({ env }: { env: string }) {
  const color = ENV_COLORS[env] ?? '#6B7280'
  return (
    <span className={styles.badge} style={{ '--bc': color } as React.CSSProperties}>
      {env === 'PRODUCTION' ? 'PROD' : env}
    </span>
  )
}

function CritBadge({ crit }: { crit: string }) {
  const color = CRIT_COLORS[crit] ?? '#6B7280'
  return <span className={styles.badge} style={{ '--bc': color } as React.CSSProperties}>{crit}</span>
}

function ScoreBar({ score }: { score: number }) {
  const color = score >= 75 ? '#16A34A' : score >= 50 ? '#D97706' : '#DC2626'
  return (
    <div className={styles.scoreBar}>
      <div
        className={styles.scoreBarFill}
        style={{ width: `${score}%`, background: color } as React.CSSProperties}
      />
      <span className={styles.scoreBarLabel} style={{ color }}>{score}</span>
    </div>
  )
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({
  icon: Icon, label, value, sub, variant = 'neutral',
}: { icon: React.ElementType; label: string; value: string | number; sub?: string; variant?: string }) {
  return (
    <div className={`${styles.kpiCard} ${styles[`kpiCard_${variant}`]}`}>
      <div className={styles.kpiIcon}><Icon size={18} /></div>
      <div>
        <div className={styles.kpiValue}>{value}</div>
        <div className={styles.kpiLabel}>{label}</div>
        {sub && <div className={styles.kpiSub}>{sub}</div>}
      </div>
    </div>
  )
}

// ── Delete confirmation row ───────────────────────────────────────────────────

function DeleteConfirm({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className={styles.deleteConfirm}>
      <AlertTriangle size={13} />
      <span>Delete this product?</span>
      <button className={styles.confirmYes} onClick={onConfirm}>Delete</button>
      <button className={styles.confirmNo} onClick={onCancel}>Cancel</button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DisasterRecoveryPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [executive, setExecutive] = useState<Executive | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [envFilter, setEnvFilter] = useState('ALL')
  const [critFilter, setCritFilter] = useState('ALL')

  const [modalOpen, setModalOpen] = useState(false)
  const [editProduct, setEditProduct] = useState<(ProductFormData & { id: number }) | null>(null)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [pRes, eRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/products/?page_size=100`),
        fetch(`${DR_API}/api/v1/dr/dashboard/executive/`),
      ])
      if (!pRes.ok || !eRes.ok) throw new Error('API error')
      const [pData, eData] = await Promise.all([pRes.json(), eRes.json()])
      setProducts(pData.results ?? [])
      setExecutive(eData)
    } catch {
      setError('Cannot reach DR API. Make sure the dr-api service is running on port 8001.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  async function handleDelete(id: number) {
    setDeleting(true)
    try {
      await fetch(`${DR_API}/api/v1/dr/products/${id}/`, { method: 'DELETE' })
      setProducts(ps => ps.filter(p => p.id !== id))
      setDeleteId(null)
      fetchAll()
    } finally {
      setDeleting(false)
    }
  }

  function openAdd() { setEditProduct(null); setModalOpen(true) }
  function openEdit(p: Product) {
    setEditProduct({
      id: p.id,
      product_name: p.product_name,
      product_code: p.product_code,
      environment: p.environment,
      business_owner: p.business_owner,
      technical_owner: p.technical_owner,
      cost_center: '',
      primary_region: p.primary_region,
      dr_region: p.dr_region,
      azure_subscription_id: '',
      resource_group: '',
      criticality: p.criticality,
      business_impact: p.business_impact,
      recovery_sequence: p.recovery_sequence,
      dr_strategy: p.dr_strategy,
      automation_readiness: p.automation_readiness,
      rpo_target_hours: parseFloat(p.rpo_target_hours),
      rto_target_hours: parseFloat(p.rto_target_hours),
      notes: '',
    })
    setModalOpen(true)
  }

  const filtered = products.filter(p => {
    if (envFilter !== 'ALL' && p.environment !== envFilter) return false
    if (critFilter !== 'ALL' && p.criticality !== critFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        p.product_name.toLowerCase().includes(q) ||
        p.product_code.toLowerCase().includes(q) ||
        p.business_owner.toLowerCase().includes(q) ||
        p.technical_owner.toLowerCase().includes(q)
      )
    }
    return true
  })

  const isEmpty = !loading && !error && products.length === 0

  return (
    <div className={styles.page}>

      {/* ── Hero ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <Shield size={12} />
            <span>Aptean Platform</span>
            <ChevronRight size={11} />
            <span>Disaster Recovery</span>
          </div>
          <h1 className={styles.heroTitle}>Disaster Recovery Management</h1>
          <p className={styles.heroSub}>
            Centralized DR orchestration — product inventory, RPO/RTO targets, drill management,
            backup health, and compliance reporting across Azure environments.
          </p>
          <div className={styles.heroActions}>
            <button className={styles.btnPrimary} onClick={openAdd}>
              <Plus size={14} /> Add Product
            </button>
            <button className={styles.btnSecondary} onClick={fetchAll} disabled={loading}>
              <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
            </button>
          </div>
        </div>
        <div className={styles.heroBadge}>
          <ShieldAlert size={26} />
          <div className={styles.heroBadgeNum}>{executive?.total_products ?? '—'}</div>
          <div className={styles.heroBadgeSub}>Products</div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      )}

      {/* ── KPI Strip ── */}
      {executive && (
        <div className={styles.kpiRow}>
          <KpiCard icon={Package} label="Under DR Coverage" value={executive.total_products}
            sub={executive.products_without_dr > 0 ? `${executive.products_without_dr} uncovered` : 'All covered'}
            variant={executive.products_without_dr > 0 ? 'warn' : 'ok'} />
          <KpiCard icon={Activity} label="Upcoming Drills (30d)" value={executive.upcoming_drills_30d} variant="neutral" />
          <KpiCard icon={AlertTriangle} label="Overdue Actions" value={executive.overdue_action_items}
            variant={executive.overdue_action_items > 0 ? 'bad' : 'ok'} />
          <KpiCard icon={Database} label="Backup Failures (24h)" value={executive.backup_failures_24h}
            variant={executive.backup_failures_24h > 0 ? 'bad' : 'ok'} />
          <KpiCard icon={TrendingUp} label="DR Readiness Score"
            value={`${executive.dr_readiness_score}%`}
            variant={executive.dr_readiness_score >= 75 ? 'ok' : executive.dr_readiness_score >= 50 ? 'warn' : 'bad'} />
          <KpiCard icon={BarChart3} label="Compliance Score"
            value={`${executive.compliance_score}%`}
            variant={executive.compliance_score >= 75 ? 'ok' : 'warn'} />
        </div>
      )}

      {/* ── Product Inventory ── */}
      <div className={styles.inventorySection}>
        <div className={styles.inventoryHeader}>
          <div className={styles.inventoryTitle}>
            <Package size={16} />
            <h2>Product Inventory</h2>
            <span className={styles.productCount}>{products.length} products</span>
          </div>
          <div className={styles.inventoryControls}>
            <div className={styles.searchWrap}>
              <Search size={13} className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search products, owners…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button className={styles.searchClear} onClick={() => setSearch('')}>✕</button>}
            </div>
            <button
              className={`${styles.filterToggle} ${showFilters ? styles.filterToggleActive : ''}`}
              onClick={() => setShowFilters(v => !v)}
            >
              <Filter size={13} /> Filters <ChevronDown size={12} />
            </button>
            <button className={styles.btnPrimary} onClick={openAdd}>
              <Plus size={13} /> Add Product
            </button>
          </div>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Environment:</span>
              {['ALL', 'PRODUCTION', 'UAT', 'QA', 'DEV', 'DEMO'].map(v => (
                <button
                  key={v}
                  className={`${styles.filterChip} ${envFilter === v ? styles.filterChipActive : ''}`}
                  onClick={() => setEnvFilter(v)}
                >
                  {v === 'ALL' ? 'All' : v === 'PRODUCTION' ? 'PROD' : v}
                </button>
              ))}
            </div>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Criticality:</span>
              {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(v => (
                <button
                  key={v}
                  className={`${styles.filterChip} ${critFilter === v ? styles.filterChipActive : ''}`}
                  onClick={() => setCritFilter(v)}
                >
                  {v === 'ALL' ? 'All' : v}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className={styles.loadingRow}>
            <RefreshCw size={18} className={styles.spin} />
            <span>Loading products…</span>
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><ShieldAlert size={36} /></div>
            <h3>No products in DR inventory yet</h3>
            <p>Add your first product to start tracking RPO/RTO targets and scheduling DR drills.</p>
            <div className={styles.emptySteps}>
              {[
                { n: '1', t: 'Add Product', d: 'Register product with DR strategy and owners' },
                { n: '2', t: 'Set Targets', d: 'Define RPO/RTO recovery objectives' },
                { n: '3', t: 'Schedule Drill', d: 'Plan and run your first DR drill' },
                { n: '4', t: 'Track Compliance', d: 'Monitor scores and generate reports' },
              ].map(s => (
                <div key={s.n} className={styles.emptyStep}>
                  <div className={styles.emptyStepNum}>{s.n}</div>
                  <div>
                    <div className={styles.emptyStepTitle}>{s.t}</div>
                    <div className={styles.emptyStepDesc}>{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <button className={styles.btnPrimary} onClick={openAdd}>
              <Plus size={14} /> Add First Product
            </button>
          </div>
        )}

        {/* Product table */}
        {!loading && !isEmpty && (
          <>
            {filtered.length === 0 && !isEmpty && (
              <div className={styles.noMatch}>
                No products match your filters.{' '}
                <button onClick={() => { setSearch(''); setEnvFilter('ALL'); setCritFilter('ALL') }}>
                  Clear filters
                </button>
              </div>
            )}
            {filtered.length > 0 && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Criticality</th>
                      <th>DR Strategy</th>
                      <th>Primary → DR Region</th>
                      <th>RPO / RTO</th>
                      <th>Automation</th>
                      <th>Score</th>
                      <th>Owners</th>
                      <th className={styles.actionsCol}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.id} className={deleteId === p.id ? styles.rowDeleting : ''}>
                        <td>
                          <div className={styles.productCell}>
                            <span className={styles.productName}>{p.product_name}</span>
                            <div className={styles.productMeta}>
                              {p.product_code && <code className={styles.productCode}>{p.product_code}</code>}
                              <EnvBadge env={p.environment} />
                            </div>
                          </div>
                        </td>
                        <td><CritBadge crit={p.criticality} /></td>
                        <td>
                          <span className={styles.strategyLabel}>
                            {STRATEGY_LABELS[p.dr_strategy] ?? p.dr_strategy}
                          </span>
                        </td>
                        <td>
                          {p.primary_region && p.dr_region
                            ? <span className={styles.regionPair}>{p.primary_region} → {p.dr_region}</span>
                            : <span className={styles.notSet}>Not configured</span>}
                        </td>
                        <td>
                          <span className={styles.rpoRto}>
                            {parseFloat(p.rpo_target_hours)}h / {parseFloat(p.rto_target_hours)}h
                          </span>
                        </td>
                        <td>
                          <span className={styles.autoLabel}>{AUTO_LABELS[p.automation_readiness] ?? p.automation_readiness}</span>
                        </td>
                        <td><ScoreBar score={p.dr_readiness_score} /></td>
                        <td>
                          <div className={styles.ownersCell}>
                            {p.business_owner
                              ? <span title={p.business_owner} className={styles.ownerTag}>B: {p.business_owner.split('@')[0]}</span>
                              : <span className={styles.notSet}>—</span>}
                            {p.technical_owner
                              ? <span title={p.technical_owner} className={styles.ownerTag}>T: {p.technical_owner.split('@')[0]}</span>
                              : null}
                          </div>
                        </td>
                        <td>
                          {deleteId === p.id
                            ? <DeleteConfirm
                                onConfirm={() => handleDelete(p.id)}
                                onCancel={() => setDeleteId(null)}
                              />
                            : (
                              <div className={styles.rowActions}>
                                <button
                                  className={styles.editBtn}
                                  onClick={() => openEdit(p)}
                                  title="Edit product"
                                  disabled={deleting}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className={styles.deleteBtn}
                                  onClick={() => setDeleteId(p.id)}
                                  title="Delete product"
                                  disabled={deleting}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── DR Module Overview ── */}
      <div className={styles.moduleSection}>
        <div className={styles.moduleSectionHeader}>
          <ShieldAlert size={15} />
          <h2>DR Platform Modules</h2>
          <span className={styles.phaseBadge}>Phase 1 — In Progress</span>
        </div>
        <div className={styles.moduleGrid}>
          {[
            { icon: Package,       label: 'Product Inventory',     detail: 'Master DR registry',              status: 'active', wp: 'WP01-02' },
            { icon: TrendingUp,    label: 'RPO/RTO Tracking',      detail: 'Target vs actual, SLA trends',    status: 'planned', wp: 'WP03' },
            { icon: Activity,      label: 'DR Drill Management',   detail: 'Drills, postmortems, actions',    status: 'planned', wp: 'WP04' },
            { icon: Clock,         label: 'Drill Templates',       detail: 'Reusable checklists',             status: 'planned', wp: 'WP05' },
            { icon: Database,      label: 'Backup Status',         detail: 'Azure Backup integration',        status: 'planned', wp: 'WP06' },
            { icon: BarChart3,     label: 'Dashboard & Reports',   detail: 'KPIs, PDF/XLSX export',           status: 'planned', wp: 'WP07' },
            { icon: AlertTriangle, label: 'Notifications',         detail: 'Teams, Slack, PagerDuty',         status: 'planned', wp: 'WP08' },
            { icon: Shield,        label: 'RBAC & Audit Log',      detail: '7 roles, SSO, immutable trail',   status: 'planned', wp: 'WP09' },
          ].map(m => (
            <div key={m.label} className={`${styles.moduleCard} ${styles[`moduleCard_${m.status}`]}`}>
              <div className={styles.moduleCardIcon}><m.icon size={18} /></div>
              <div className={styles.moduleCardBody}>
                <div className={styles.moduleCardLabel}>{m.label}</div>
                <div className={styles.moduleCardDetail}>{m.detail}</div>
              </div>
              <div className={styles.moduleCardRight}>
                {m.status === 'active'
                  ? <CheckCircle2 size={14} className={styles.activeIcon} />
                  : <span className={styles.plannedTag}>{m.wp}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modal ── */}
      {modalOpen && (
        <AddProductModal
          initialData={editProduct ?? undefined}
          onSaved={() => { setModalOpen(false); setEditProduct(null); fetchAll() }}
          onClose={() => { setModalOpen(false); setEditProduct(null) }}
        />
      )}
    </div>
  )
}
